import { colors as CropCircleColors } from '../components/crop-circle/tokens/colors';
import { role } from './roleTokens';

/**
 * Application color palette (Crop Circle design system).
 *
 * Two vocabularies live side by side while the role-token migration runs
 * (plan 0028). The hue-named keys below are the legacy single-theme surface;
 * `Colors.role.*` names what a colour is *for* and is the only vocabulary
 * that can flip with the theme. On web, `role` resolves to CSS variables
 * (see `roleTokens.web.ts`); on native it is the static light palette.
 * New code should read `Colors.role`; hue-named keys shrink as features
 * migrate.
 */
export const Colors = {
  ...CropCircleColors,
  role,
  primary: CropCircleColors.brand.core, // #FF5505
  primaryDark: CropCircleColors.orange[600], // #C44A0A
  primaryWithAlpha: `${CropCircleColors.brand.core}1A`, // rgba(255, 85, 5, 0.1)
  primaryLight: CropCircleColors.orange[100], // #FFF0E5

  white: CropCircleColors.brand.white, // #FCFCFC
  black: CropCircleColors.brand.black, // #020202
  blackText: CropCircleColors.system.textPrimary, // #2F2F2F
  blackWithAlpha: `${CropCircleColors.neutral[800]}1A`, // rgba(2, 2, 2, 0.1)
  blackInactive: `${CropCircleColors.neutral[800]}BF`, // rgba(2, 2, 2, 0.75)
  blackOverlay: `${CropCircleColors.neutral[800]}4D`, // rgba(2, 2, 2, 0.3)
  whiteOverlay: `${CropCircleColors.brand.white}E6`, // rgba(252, 252, 252, 0.9)

  primaryGrayBackground: CropCircleColors.neutral[200], // #F3F3F3
  grayText: CropCircleColors.system.textSecondary, // #6C6C6C
  grayFill: CropCircleColors.system.muted, // #9FA6B2
  graySpacer: CropCircleColors.neutral[300], // #D8D8D8
  grayDivider: CropCircleColors.neutral[400], // #B4B4B4
  grayButtonBorder: CropCircleColors.system.borderDefault, // #D8D8D8
  grayDisabled: CropCircleColors.system.disabled, // #CCCCCC
  primaryGrayHeader: CropCircleColors.neutral[300], // #D8D8D8
  secondaryGrayHeader: CropCircleColors.slate[100], // #ECEEF1
  secondaryGreyBorder: CropCircleColors.neutral[300], // #D8D8D8
  secondaryGreyBackground: CropCircleColors.neutral[100], // #FCFCFC

  unreadBackground: CropCircleColors.semantic.warningLight, // #FFF4C1
  chipBackground: CropCircleColors.orange[200], // #FFD7B0
  redBackground: CropCircleColors.semantic.errorLight, // #FFD9D2

  danger: CropCircleColors.semantic.error, // #E24D2A
  warning: CropCircleColors.semantic.warning, // #F9C431
  satisfactory: CropCircleColors.semantic.success, // #36B37E
  info: CropCircleColors.semantic.info, // #4A90E2

  inactive: `${CropCircleColors.semantic.error}59`, // rgba(226, 77, 42, 0.35)
  active: CropCircleColors.semantic.successLight, // #D3F4E7
  orangeActive: CropCircleColors.orange[200], // #FFD7B0
  orangeActiveHovered: CropCircleColors.orange[300], // #FFB368
  orangeDark: CropCircleColors.orange[700], // #872D05

  secondaryContainer: 'transparent',
  tabActive: CropCircleColors.neutral[800], // #020202
  tabInactive: CropCircleColors.system.textSecondary, // #6C6C6C

  redText: CropCircleColors.semantic.errorDark, // #B32A12

  // issue severities
  red: CropCircleColors.semantic.error, // #E24D2A
  yellow: CropCircleColors.semantic.warning, // #F9C431
  green: CropCircleColors.semantic.success, // #36B37E
  blue: CropCircleColors.semantic.info, // #4A90E2
  blueLight: CropCircleColors.semantic.infoLight, // #D0E5F9
  purple: CropCircleColors.label.violet, // #7E4CC9
  darkPink: CropCircleColors.label.magenta, // #D649A6

  // mentions list
  mentionName: CropCircleColors.system.textPrimary, // #2F2F2F
  mentionEmail: CropCircleColors.system.textSecondary, // #6C6C6C
};
